// frontend/src/pages/Asset.js

import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import AppHeader from '../components/AppHeader'; 
import { AuthService, WithdrawalService, RechargeService, FundHistoryService } from "../services";
import './Asset.css';

const Asset = (props) => {
  const [activeTab, setActiveTab] = useState('Recharges');
  const [assetData, setAssetData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const currentUser = AuthService.getCurrentUser();

  const tabTitles = ['Recharges', 'Withdraws', 'Funds'];

  // Load data only once when component mounts or tab changes
  useEffect(() => {
    if (!currentUser) {
      props.history.push("/login");
      return;
    }
    
    // Only load if not already loaded for this tab
    if (!hasLoaded) {
      loadAssetData();
    }
  }, [activeTab, currentUser]);

  // Reset hasLoaded when tab changes
  useEffect(() => {
    setHasLoaded(false);
    setAssetData([]);
  }, [activeTab]);

  const loadAssetData = async () => {
    if (hasLoaded) return;
    
    setLoading(true);
    try {
      let data = [];
      
      if (activeTab === 'Withdraws') {
        // Fetch withdrawal history
        const response = await WithdrawalService.getUserWithdrawals();
        data = (response.data || []).map(withdrawal => ({
          id: withdrawal._id,
          amount: withdrawal.amount.toFixed(2),
          status: withdrawal.status || 'Pending',
          statusColor: withdrawal.status === 'Approved' ? 'text-green' : 
                       withdrawal.status === 'Rejected' ? 'text-red' : 'text-yellow',
          time: new Date(withdrawal.createdAt).toLocaleString(),
          walletType: withdrawal.walletType,
          walletDetails: withdrawal.walletDetails
        }));
      } else if (activeTab === 'Recharges') {
        // Fetch recharge history
        try {
          const response = await RechargeService.getUserRecharges();
          data = (response.data || []).map(recharge => ({
            id: recharge._id,
            amount: recharge.amount.toFixed(2),
            status: recharge.status || 'Pending',
            statusColor: recharge.status === 'Approved' ? 'text-green' : 
                         recharge.status === 'Rejected' ? 'text-red' : 'text-yellow',
            time: new Date(recharge.createdAt).toLocaleString()
          }));
        } catch (err) {
          console.error("Error loading recharges:", err);
          data = [];
        }
      } else if (activeTab === 'Funds') {
        // Fetch fund history
        try {
          const response = await FundHistoryService.getUserFundHistory();
          data = (response.data || []).map(fund => ({
            id: fund._id,
            type: 'Funds',
            amount: fund.amount > 0 ? `+${fund.amount.toFixed(2)}` : `-${Math.abs(fund.amount).toFixed(2)}`,
            fundType: fund.fundType || 'Unknown',
            time: new Date(fund.createdAt).toLocaleString(),
            description: fund.description || '',
            balanceAfter: fund.balanceAfter || 0
          }));
        } catch (err) {
          console.error("Error loading fund history:", err);
          data = [];
        }
      }
      
      setAssetData(data);
      setLoading(false);
      setHasLoaded(true);
      
    } catch (error) {
      console.error(`Error loading ${activeTab}:`, error);
      setAssetData([]);
      setLoading(false);
      setHasLoaded(true);
    }
  };

  // Handle manual refresh if needed
  const handleRefresh = () => {
    setHasLoaded(false);
    setAssetData([]);
    loadAssetData();
  };

  return (
    <div className="asset-container">
      <AppHeader />

      <Container className="mt-0">
        <div className="page-header">
          <div className="header-title">Asset</div>
          <div className="header-filter">All Records <span className="arrow-down">▼</span></div>
        </div>

        {/* Tabs */}
        <div className="tabs-header">
          {tabTitles.map((tab) => (
            <div 
              key={tab}
              className={`tab-item ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </div>
          ))}
        </div>

        {/* List Content */}
        <div className="asset-list">
          {loading ? (
            <div className="empty-state">
              <div className="loading-spinner"></div>
              <h4 style={{color: '#848e9c', marginTop: '15px'}}>Loading...</h4>
            </div>
          ) : assetData.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '48px', color: '#848e9c', marginBottom: '15px' }}>
                {activeTab === 'Recharges' ? '💰' : activeTab === 'Withdraws' ? '💸' : '📊'}
              </div>
              <h4 style={{color: '#ffffff', marginBottom: '10px'}}>No data available</h4>
              <p style={{color: '#848e9c', fontSize: '14px', maxWidth: '300px', lineHeight: '1.5'}}>
                {activeTab === 'Recharges' 
                  ? 'You don\'t have any recharge records yet.' 
                  : activeTab === 'Withdraws'
                  ? 'You don\'t have any withdrawal records yet.'
                  : 'You don\'t have any fund history records yet.'
                }
              </p>
              <button 
                onClick={handleRefresh}
                style={{
                  marginTop: '20px',
                  padding: '8px 20px',
                  backgroundColor: '#0ecb81',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Refresh
              </button>
            </div>
          ) : (
            assetData.map((item) => (
              <div className="asset-card" key={item.id}>
                {item.type === 'Funds' ? (
                  /* Funds Layout */
                  <>
                    <div className="card-row">
                      <span className="label">Fund Amount</span>
                      <span className={`value fund-amount ${item.amount.startsWith('+') ? 'text-red' : 'text-blue'}`}>
                        {item.amount}
                      </span>
                    </div>
                    <div className="card-row">
                      <span className="label">Fund Type</span>
                      <span className="value text-red">{item.fundType}</span> 
                    </div>
                    <div className="card-row">
                      <span className="label">Fund Time</span>
                      <span className="value text-gray">{item.time}</span>
                    </div>
                  </>
                ) : (
                  /* Recharges/Withdraws Layout */
                  <>
                    <div className="card-row">
                      <span className="label">Amount</span>
                      <span className="value">{item.amount}</span>
                    </div>
                    <div className="card-row">
                      <span className="label">Status</span>
                      <span className={`value status-text ${item.statusColor}`}>
                        {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                      </span>
                    </div>
                    <div className="card-row">
                      <span className="label">Apply Time</span>
                      <span className="value text-gray">{item.time}</span>
                    </div>
                  </>
                )}
                <div className="card-arrow">›</div>
              </div>
            ))
          )}
        </div>
      </Container>
    </div>
  );
};

export default Asset;